import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Filter, X, Users, RefreshCw, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight, DollarSign, TrendingUp, TrendingDown, Wallet, Eye, Download, ShoppingCart, Truck, Calendar, FileText, Loader, History } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import authUtils from '../utils/authUtils';

const CongNoManagement = () => {
    // State Management
    const [customers, setCustomers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [vanData, setVanData] = useState([]);
    const [dominoData, setDominoData] = useState([]);
    const [phuPhamData, setPhuPhamData] = useState([]);
    const [congNoDauKyData, setCongNoDauKyData] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filterPhanLoai, setFilterPhanLoai] = useState('KH');
    const [sortConfig, setSortConfig] = useState({ key: 'debt', direction: 'descending' });

    // Date filter states
    const [filterFromDate, setFilterFromDate] = useState('');
    const [filterToDate, setFilterToDate] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Detail Modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('payments');

    // Format currency
    const formatCurrency = (value) => {
        if (!value) return '0 ₫';
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(value);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('vi-VN');
        } catch (error) {
            return dateString;
        }
    };

    // Fetch all data
    const fetchAllData = async () => {
        try {
            setIsLoading(true);

            const [dskhRes, paymentRes, vanRes, dominoRes, phuPhamRes, congNoDauKyRes] = await Promise.all([
                authUtils.apiRequestKHO('DSKH', 'Find', {}),
                authUtils.apiRequestKHO('THANH_TOAN', 'Find', {}),
                authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {}),
                authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Find', {}),
                authUtils.apiRequest('XUAT_PHUPHAM', 'Find', {}),
                authUtils.apiRequestKHO('CONGNO_DAUKY', 'Find', {})
            ]);

            setCustomers(Array.isArray(dskhRes) ? dskhRes : []);
            setPayments(Array.isArray(paymentRes) ? paymentRes : []);
            setVanData(Array.isArray(vanRes) ? vanRes : []);
            setDominoData(Array.isArray(dominoRes) ? dominoRes : []);
            setPhuPhamData(Array.isArray(phuPhamRes) ? phuPhamRes : []);
            setCongNoDauKyData(Array.isArray(congNoDauKyRes) ? congNoDauKyRes : []);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Lỗi khi tải dữ liệu');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Helper function to check if date is in range
    const isDateInRange = (dateString, fromDate, toDate) => {
        if (!dateString) return true;
        if (!fromDate && !toDate) return true;

        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);

        if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            if (date < from) return false;
        }

        if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            if (date > to) return false;
        }

        return true;
    };

    // Calculate debt for all customers with date filter
    const customersWithDebt = useMemo(() => {
        const normalize = (str) => str?.trim().toLowerCase();

        const refinedMap = new Map();
        const getEntry = (name) => {
            const key = normalize(name);
            if (!key) return null;
            if (!refinedMap.has(key)) {
                refinedMap.set(key, { nhap: 0, xuat: 0, thu: 0, chi: 0, hoan: 0, congNoDauKy: 0 });
            }
            return refinedMap.get(key);
        };

        // Process Van Data (with date filter)
        vanData.forEach(v => {
            if (!isDateInRange(v.NGAYNHAP_XUAT, filterFromDate, filterToDate)) return;
            const entry = getEntry(v.NCC_KHACHHANG);
            if (entry) {
                const val = parseFloat(v.TONGTIEN) || 0;
                if (v.NGHIEP_VU === 'NHAP') entry.nhap += val;
                else if (v.NGHIEP_VU === 'XUAT') entry.xuat += val;
            }
        });

        // Process Domino Data (with date filter)
        dominoData.forEach(v => {
            if (!isDateInRange(v.NGAYNHAP_XUAT, filterFromDate, filterToDate)) return;
            const entry = getEntry(v.NCC_KHACHHANG);
            if (entry) {
                const val = parseFloat(v.TONGTIEN) || 0;
                if (v.NGHIEP_VU === 'NHAP') entry.nhap += val;
                else if (v.NGHIEP_VU === 'XUAT') entry.xuat += val;
            }
        });

        // Process Phu Pham Data (with date filter)
        phuPhamData.forEach(v => {
            if (!isDateInRange(v.NGAYXUAT, filterFromDate, filterToDate)) return;
            const entry = getEntry(v.NCC_KHACHHANG);
            if (entry) {
                const val = parseFloat(v.TONGTIEN) || 0;
                entry.xuat += val;
            }
        });

        // Process Payments (with date filter)
        payments.forEach(p => {
            if (!isDateInRange(p.NGAY_THANHTOAN, filterFromDate, filterToDate)) return;
            const entry = getEntry(p.TEN_KHACHHANG);
            if (entry) {
                const val = parseFloat(p.SOTIEN_THANHTOAN) || 0;
                if (p.LOAI_THANHTOAN === 'THU') entry.thu += val;
                else if (p.LOAI_THANHTOAN === 'CHI') entry.chi += val;
                else if (p.LOAI_THANHTOAN === 'HOAN_TIEN') entry.hoan += val;
            }
        });

        // Process Initial Debt (with date filter on NGAY_CONGNO)
        congNoDauKyData.forEach(cn => {
            if (!isDateInRange(cn.NGAY_CONGNO, filterFromDate, filterToDate)) return;
            const entry = getEntry(cn.TEN_KHACHHANG);
            if (entry) {
                const val = parseFloat(cn.TONGTIEN_CONGNO_DAUKY) || 0;
                entry.congNoDauKy += val;
            }
        });

        // Map Customers
        return customers.map(c => {
            const key = normalize(c.TEN_KHACHHANG);
            const stats = refinedMap.get(key) || { nhap: 0, xuat: 0, thu: 0, chi: 0, hoan: 0, congNoDauKy: 0 };

            let totalPhatSinh = 0;
            let totalPaid = 0;

            if (c.PHAN_LOAI === 'NCC') {
                totalPhatSinh = stats.nhap;
                totalPaid = stats.chi;
                if (stats.hoan) totalPaid -= stats.hoan;
            } else {
                totalPhatSinh = stats.xuat;
                totalPaid = stats.thu;
                if (stats.hoan) totalPaid -= stats.hoan;
            }

            const debt = stats.congNoDauKy + totalPhatSinh - totalPaid;

            return {
                ...c,
                congNoDauKy: stats.congNoDauKy,
                totalPhatSinh,
                totalPaid,
                debt
            };
        });
    }, [customers, vanData, dominoData, phuPhamData, payments, congNoDauKyData, filterFromDate, filterToDate]);

    // Filter and sort
    const filteredCustomers = useMemo(() => {
        let result = customersWithDebt.filter(customer => {
            const searchLower = search.toLowerCase();
            const matchSearch = (
                customer['MA_KH']?.toLowerCase().includes(searchLower) ||
                customer['TEN_KHACHHANG']?.toLowerCase().includes(searchLower) ||
                customer['TEN_VIET_TAT']?.toLowerCase().includes(searchLower)
            );
            const matchPhanLoai = customer['PHAN_LOAI'] === filterPhanLoai;
            // Chỉ hiển thị khách hàng có công nợ > 0 hoặc có phát sinh
            const hasDebt = customer.debt !== 0 || customer.totalPhatSinh > 0 || customer.congNoDauKy > 0;

            return matchSearch && matchPhanLoai && hasDebt;
        });

        // Sort
        if (sortConfig.key) {
            result.sort((a, b) => {
                const valA = a[sortConfig.key] || 0;
                const valB = b[sortConfig.key] || 0;
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [customersWithDebt, search, filterPhanLoai, sortConfig]);

    // Statistics
    const statistics = useMemo(() => {
        const khCustomers = customersWithDebt.filter(c => c.PHAN_LOAI === 'KH');
        const nccCustomers = customersWithDebt.filter(c => c.PHAN_LOAI === 'NCC');

        const totalDebtKH = khCustomers.reduce((sum, c) => sum + (c.debt > 0 ? c.debt : 0), 0);
        const totalDebtNCC = nccCustomers.reduce((sum, c) => sum + (c.debt > 0 ? c.debt : 0), 0);
        const totalPaidKH = khCustomers.reduce((sum, c) => sum + c.totalPaid, 0);
        const totalPaidNCC = nccCustomers.reduce((sum, c) => sum + c.totalPaid, 0);
        const countWithDebt = customersWithDebt.filter(c => c.debt > 0).length;

        return {
            countWithDebt,
            totalDebtKH,
            totalDebtNCC,
            totalPaid: totalPaidKH + totalPaidNCC
        };
    }, [customersWithDebt]);

    // Pagination
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, filterPhanLoai, itemsPerPage, filterFromDate, filterToDate]);

    // Pagination handlers
    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(totalPages);
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToPage = (page) => setCurrentPage(page);

    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    // Sort handler
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="text-gray-400 ml-1">⇅</span>;
        }
        return sortConfig.direction === 'ascending' ?
            <span className="text-blue-600 ml-1">↑</span> :
            <span className="text-blue-600 ml-1">↓</span>;
    };

    // Refresh
    const handleRefresh = async () => {
        toast.info('Đang tải lại dữ liệu...');
        await fetchAllData();
        toast.success('Đã tải lại dữ liệu!');
    };

    // Export Excel
    const handleExportExcel = () => {
        const dataToExport = filteredCustomers.map(c => ({
            'Mã KH': c.MA_KH,
            'Tên': c.TEN_KHACHHANG,
            'Loại': c.PHAN_LOAI === 'KH' ? 'Khách hàng' : 'Nhà cung cấp',
            'Công nợ đầu kỳ': c.congNoDauKy,
            'Tổng phát sinh': c.totalPhatSinh,
            'Đã thanh toán': c.totalPaid,
            'Còn nợ': c.debt
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Công Nợ');

        // Set column widths
        ws['!cols'] = [
            { wch: 15 },
            { wch: 40 },
            { wch: 15 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 }
        ];

        const fileName = `CongNo_${filterPhanLoai}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success('Đã xuất file Excel!');
    };

    // Open detail modal
    const handleOpenDetail = (customer) => {
        setSelectedCustomer(customer);
        setActiveDetailTab('payments');
        setShowDetailModal(true);
    };

    // Get payment history for customer
    const getCustomerPayments = (customerName) => {
        return payments.filter(p =>
            p.TEN_KHACHHANG?.trim().toLowerCase() === customerName?.trim().toLowerCase()
        );
    };

    // Get transaction history for customer
    const getCustomerTransactions = (customerName, phanLoai) => {
        const normalize = (str) => str?.trim().toLowerCase();
        const name = normalize(customerName);

        const vanTrans = vanData.filter(v =>
            normalize(v.NCC_KHACHHANG) === name &&
            (phanLoai === 'KH' ? v.NGHIEP_VU === 'XUAT' : v.NGHIEP_VU === 'NHAP')
        ).map(v => ({ ...v, source: 'Ván' }));

        const dominoTrans = dominoData.filter(v =>
            normalize(v.NCC_KHACHHANG) === name &&
            (phanLoai === 'KH' ? v.NGHIEP_VU === 'XUAT' : v.NGHIEP_VU === 'NHAP')
        ).map(v => ({ ...v, source: 'Domino' }));

        const phuPhamTrans = phanLoai === 'KH' ? phuPhamData.filter(v =>
            normalize(v.NCC_KHACHHANG) === name
        ).map(v => ({ ...v, source: 'Phụ phẩm' })) : [];

        return [...vanTrans, ...dominoTrans, ...phuPhamTrans].sort((a, b) =>
            new Date(b.NGAYNHAP_XUAT || b.NGAYXUAT) - new Date(a.NGAYNHAP_XUAT || a.NGAYXUAT)
        );
    };

    // Badge for PHAN_LOAI
    const getPhanLoaiBadge = (phanLoai) => {
        if (phanLoai === 'NCC') {
            return (
                <span className="px-2 py-1 bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 rounded text-xs font-semibold flex items-center gap-1 w-fit">
                    <Truck className="w-3 h-3" />
                    NCC
                </span>
            );
        }
        return (
            <span className="px-2 py-1 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded text-xs font-semibold flex items-center gap-1 w-fit">
                <ShoppingCart className="w-3 h-3" />
                KH
            </span>
        );
    };

    // Count by type
    const khCount = customersWithDebt.filter(c => c.PHAN_LOAI === 'KH' && (c.debt !== 0 || c.totalPhatSinh > 0)).length;
    const nccCount = customersWithDebt.filter(c => c.PHAN_LOAI === 'NCC' && (c.debt !== 0 || c.totalPhatSinh > 0)).length;

    return (
        <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="p-4 md:p-5">
                        {/* Header */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-md">
                                    <DollarSign className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-gray-800">Quản Lý Công Nợ</h1>
                                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">Tổng hợp công nợ khách hàng và nhà cung cấp</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={handleRefresh}
                                    className="px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-all shadow-sm text-sm"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span className="hidden sm:inline">Làm mới</span>
                                </button>
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-all shadow-sm text-sm ${showFilters
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    <Filter className="w-4 h-4" />
                                    {showFilters ? "Ẩn tìm kiếm" : "Tìm kiếm"}
                                </button>
                                <button
                                    onClick={handleExportExcel}
                                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-all shadow-md text-sm"
                                >
                                    <Download className="w-4 h-4" />
                                    Xuất Excel
                                </button>
                            </div>
                        </div>

                        {/* Search */}
                        {showFilters && (
                            <div className="mb-4 animate-fadeIn space-y-3">
                                {/* Search */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Tìm kiếm theo mã KH, tên, tên viết tắt..."
                                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>

                                {/* Date Filter */}
                                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-500" />
                                        <span className="text-sm text-gray-600 font-medium">Lọc theo ngày:</span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs text-gray-500">Từ:</label>
                                            <input
                                                type="date"
                                                value={filterFromDate}
                                                onChange={(e) => setFilterFromDate(e.target.value)}
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs text-gray-500">Đến:</label>
                                            <input
                                                type="date"
                                                value={filterToDate}
                                                onChange={(e) => setFilterToDate(e.target.value)}
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        {(filterFromDate || filterToDate) && (
                                            <button
                                                onClick={() => {
                                                    setFilterFromDate('');
                                                    setFilterToDate('');
                                                }}
                                                className="px-3 py-1.5 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all flex items-center gap-1"
                                            >
                                                <X className="w-3 h-3" />
                                                Xóa lọc
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Active filter indicator */}
                                {(filterFromDate || filterToDate) && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-700 flex items-center gap-2">
                                        <Filter className="w-4 h-4" />
                                        <span>
                                            Đang lọc: {filterFromDate ? `từ ${new Date(filterFromDate).toLocaleDateString('vi-VN')}` : ''}
                                            {filterFromDate && filterToDate ? ' - ' : ''}
                                            {filterToDate ? `đến ${new Date(filterToDate).toLocaleDateString('vi-VN')}` : ''}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Statistics Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <div className="group relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                            <Users className="w-5 h-5 text-white" />
                                        </div>
                                        <p className="text-3xl font-bold text-white">{statistics.countWithDebt}</p>
                                    </div>
                                    <h3 className="text-xs font-semibold text-blue-100 uppercase tracking-wide">Có công nợ</h3>
                                </div>
                            </div>

                            <div className="group relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                            <TrendingUp className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-red-100 uppercase tracking-wide">Phải thu (KH)</h3>
                                    </div>
                                    <p className="text-xl font-bold text-white">{formatCurrency(statistics.totalDebtKH)}</p>
                                </div>
                            </div>

                            <div className="group relative bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                            <TrendingDown className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-orange-100 uppercase tracking-wide">Phải trả (NCC)</h3>
                                    </div>
                                    <p className="text-xl font-bold text-white">{formatCurrency(statistics.totalDebtNCC)}</p>
                                </div>
                            </div>

                            <div className="group relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                            <Wallet className="w-5 h-5 text-white" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-emerald-100 uppercase tracking-wide">Đã thanh toán</h3>
                                    </div>
                                    <p className="text-xl font-bold text-white">{formatCurrency(statistics.totalPaid)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 mb-4">
                            <button
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${filterPhanLoai === 'KH'
                                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                                onClick={() => setFilterPhanLoai('KH')}
                            >
                                <ShoppingCart className="w-4 h-4" />
                                Khách Hàng
                                <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                    {khCount}
                                </span>
                            </button>
                            <button
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${filterPhanLoai === 'NCC'
                                    ? 'border-orange-600 text-orange-600 bg-orange-50/50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                                onClick={() => setFilterPhanLoai('NCC')}
                            >
                                <Truck className="w-4 h-4" />
                                Nhà Cung Cấp
                                <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                    {nccCount}
                                </span>
                            </button>
                        </div>

                        {/* Table - Desktop */}
                        <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-20">
                                    <div className="text-center">
                                        <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                                        <p className="text-gray-600">Đang tải dữ liệu...</p>
                                    </div>
                                </div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Loại</th>
                                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('MA_KH')}>
                                                Mã {getSortIcon('MA_KH')}
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('TEN_KHACHHANG')}>
                                                Tên {getSortIcon('TEN_KHACHHANG')}
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('congNoDauKy')}>
                                                Đầu kỳ {getSortIcon('congNoDauKy')}
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('totalPhatSinh')}>
                                                Phát sinh {getSortIcon('totalPhatSinh')}
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('totalPaid')}>
                                                Đã TT {getSortIcon('totalPaid')}
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200" onClick={() => requestSort('debt')}>
                                                Còn nợ {getSortIcon('debt')}
                                            </th>
                                            <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {currentItems.length > 0 ? (
                                            currentItems.map((customer, index) => (
                                                <tr key={customer['MA_KH']} className={`hover:bg-blue-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                                    <td className="px-3 py-2">{getPhanLoaiBadge(customer['PHAN_LOAI'])}</td>
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <span className="px-2 py-1 bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 rounded text-xs font-semibold">
                                                            {customer['MA_KH']}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm font-medium text-gray-900 max-w-[250px]">
                                                        <div className="break-words">{customer['TEN_KHACHHANG']}</div>
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-right text-gray-700">
                                                        {formatCurrency(customer.congNoDauKy)}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-right text-blue-600 font-medium">
                                                        {formatCurrency(customer.totalPhatSinh)}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-right text-emerald-600 font-medium">
                                                        {formatCurrency(customer.totalPaid)}
                                                    </td>
                                                    <td className="px-3 py-2 text-right">
                                                        <span className={`text-sm font-bold ${customer.debt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                            {formatCurrency(customer.debt)}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-center">
                                                        <button
                                                            onClick={() => handleOpenDetail(customer)}
                                                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-lg hover:bg-blue-100 transition-all transform hover:scale-110"
                                                            title="Xem chi tiết"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="px-6 py-12 text-center">
                                                    <div className="flex flex-col items-center justify-center text-gray-500">
                                                        <DollarSign className="w-16 h-16 text-gray-300 mb-4" />
                                                        <p className="text-lg font-medium">Không có dữ liệu công nợ</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Card View - Mobile */}
                        <div className="lg:hidden space-y-3">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-20">
                                    <RefreshCw className="w-12 h-12 text-blue-600 animate-spin" />
                                </div>
                            ) : currentItems.length > 0 ? (
                                currentItems.map((customer) => (
                                    <div key={customer['MA_KH']} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    {getPhanLoaiBadge(customer['PHAN_LOAI'])}
                                                    <span className="px-2 py-0.5 bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-800 rounded text-xs font-semibold">
                                                        {customer['MA_KH']}
                                                    </span>
                                                </div>
                                                <h3 className="font-semibold text-gray-900 text-sm">{customer['TEN_KHACHHANG']}</h3>
                                            </div>
                                            <button
                                                onClick={() => handleOpenDetail(customer)}
                                                className="text-blue-600 hover:text-blue-900 p-1.5 rounded-lg hover:bg-blue-100 transition-all"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="bg-gray-50 rounded p-2">
                                                <span className="text-gray-500">Đầu kỳ:</span>
                                                <p className="font-semibold text-gray-700">{formatCurrency(customer.congNoDauKy)}</p>
                                            </div>
                                            <div className="bg-blue-50 rounded p-2">
                                                <span className="text-gray-500">Phát sinh:</span>
                                                <p className="font-semibold text-blue-600">{formatCurrency(customer.totalPhatSinh)}</p>
                                            </div>
                                            <div className="bg-green-50 rounded p-2">
                                                <span className="text-gray-500">Đã TT:</span>
                                                <p className="font-semibold text-emerald-600">{formatCurrency(customer.totalPaid)}</p>
                                            </div>
                                            <div className={`rounded p-2 ${customer.debt > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                                                <span className="text-gray-500">Còn nợ:</span>
                                                <p className={`font-bold ${customer.debt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                    {formatCurrency(customer.debt)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center justify-center text-gray-500 py-12">
                                    <DollarSign className="w-16 h-16 text-gray-300 mb-4" />
                                    <p className="text-base font-medium">Không có dữ liệu công nợ</p>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {filteredCustomers.length > 0 && (
                            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="text-xs text-gray-600">
                                    Hiển thị {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredCustomers.length)} / {filteredCustomers.length}
                                </div>

                                <div className="flex items-center gap-1">
                                    <button onClick={goToFirstPage} disabled={currentPage === 1} className={`p-1.5 rounded-lg border transition-all ${currentPage === 1 ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                        <ChevronsLeft className="w-4 h-4" />
                                    </button>
                                    <button onClick={goToPrevPage} disabled={currentPage === 1} className={`p-1.5 rounded-lg border transition-all ${currentPage === 1 ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>

                                    <div className="hidden sm:flex items-center gap-1">
                                        {getPageNumbers().map((page, index) => (
                                            page === '...' ? (
                                                <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500 text-xs">...</span>
                                            ) : (
                                                <button
                                                    key={page}
                                                    onClick={() => goToPage(page)}
                                                    className={`min-w-[32px] px-2 py-1 rounded-lg border font-medium text-xs ${currentPage === page
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            )
                                        ))}
                                    </div>

                                    <button onClick={goToNextPage} disabled={currentPage === totalPages} className={`p-1.5 rounded-lg border transition-all ${currentPage === totalPages ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                    <button onClick={goToLastPage} disabled={currentPage === totalPages} className={`p-1.5 rounded-lg border transition-all ${currentPage === totalPages ? 'border-gray-200 text-gray-400' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                        <ChevronsRight className="w-4 h-4" />
                                    </button>
                                </div>

                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    className="px-2 py-1 border border-gray-300 rounded-lg text-xs"
                                >
                                    <option value={10}>10/trang</option>
                                    <option value={25}>25/trang</option>
                                    <option value={50}>50/trang</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && selectedCustomer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 flex justify-between items-center shadow-md z-10">
                            <div className="flex items-center gap-2">
                                <DollarSign className="w-5 h-5" />
                                <div>
                                    <h2 className="text-lg font-bold">Chi Tiết Công Nợ</h2>
                                    <p className="text-xs text-emerald-100">{selectedCustomer.TEN_KHACHHANG}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="text-white hover:bg-white/20 rounded-lg p-1 transition-all"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Summary */}
                        <div className="p-4 bg-gray-50 border-b">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white rounded-lg p-3 border">
                                    <p className="text-xs text-gray-500">Công nợ đầu kỳ</p>
                                    <p className="text-lg font-bold text-gray-700">{formatCurrency(selectedCustomer.congNoDauKy)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border">
                                    <p className="text-xs text-gray-500">Tổng phát sinh</p>
                                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedCustomer.totalPhatSinh)}</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 border">
                                    <p className="text-xs text-gray-500">Đã thanh toán</p>
                                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(selectedCustomer.totalPaid)}</p>
                                </div>
                                <div className={`rounded-lg p-3 border ${selectedCustomer.debt > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                    <p className="text-xs text-gray-500">Còn nợ</p>
                                    <p className={`text-lg font-bold ${selectedCustomer.debt > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                        {formatCurrency(selectedCustomer.debt)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 bg-white">
                            <button
                                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${activeDetailTab === 'payments'
                                    ? 'border-emerald-600 text-emerald-600 bg-emerald-50/50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => setActiveDetailTab('payments')}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    Lịch sử thanh toán
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-4 py-3 text-sm font-semibold transition-all border-b-2 ${activeDetailTab === 'transactions'
                                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                onClick={() => setActiveDetailTab('transactions')}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Phiếu phát sinh
                                </div>
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {activeDetailTab === 'payments' && (
                                <div className="space-y-2">
                                    {getCustomerPayments(selectedCustomer.TEN_KHACHHANG).length > 0 ? (
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ngày</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Số phiếu</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Loại</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Số tiền</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ghi chú</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {getCustomerPayments(selectedCustomer.TEN_KHACHHANG).map((payment, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-sm text-gray-700">{formatDate(payment.NGAY_THANHTOAN)}</td>
                                                        <td className="px-3 py-2 text-sm text-blue-600 font-medium">{payment.SOPHIEU}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            <span className={`px-2 py-1 text-xs font-semibold rounded ${payment.LOAI_THANHTOAN === 'THU' ? 'bg-green-100 text-green-800' :
                                                                payment.LOAI_THANHTOAN === 'CHI' ? 'bg-red-100 text-red-800' :
                                                                    'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {payment.LOAI_THANHTOAN}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-right font-bold text-emerald-600">
                                                            {formatCurrency(payment.SOTIEN_THANHTOAN)}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-gray-500 max-w-[200px] truncate">
                                                            {payment.GHICHU || '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="text-center py-12">
                                            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Chưa có lịch sử thanh toán</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === 'transactions' && (
                                <div className="space-y-2">
                                    {getCustomerTransactions(selectedCustomer.TEN_KHACHHANG, selectedCustomer.PHAN_LOAI).length > 0 ? (
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ngày</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Số phiếu</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Nguồn</th>
                                                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Loại</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Thành tiền</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {getCustomerTransactions(selectedCustomer.TEN_KHACHHANG, selectedCustomer.PHAN_LOAI).map((trans, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-sm text-gray-700">{formatDate(trans.NGAYNHAP_XUAT || trans.NGAYXUAT)}</td>
                                                        <td className="px-3 py-2 text-sm text-blue-600 font-medium">{trans.SOPHIEU}</td>
                                                        <td className="px-3 py-2 text-center">
                                                            <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">
                                                                {trans.source}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-center">
                                                            <span className={`px-2 py-1 text-xs font-semibold rounded ${trans.NGHIEP_VU === 'XUAT' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                                }`}>
                                                                {trans.NGHIEP_VU || 'XUAT'}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-right font-bold text-blue-600">
                                                            {formatCurrency(trans.TONGTIEN)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="text-center py-12">
                                            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500">Chưa có phiếu phát sinh</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-4 bg-gray-50">
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 text-sm"
                                >
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="light"
            />

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
            `}</style>
        </div>
    );
};

export default CongNoManagement;
